import React from 'react';

// All charts are pure SVG. Brand palette only. Nunito-everywhere typography.
// Brand tokens used here (hex equivalents):
//   primary           #34C759   (main green accent)
//   primaryHoverDark  #16ad3c   (deeper green for editorial weight)
//   primaryBg         #28a74530
//   primaryBgLight    #28a74520
//   secondary         #888EA8   (muted slate)
//   border            #E0E6ED   (light gray rule)
//   fontBlack         #111827
//   darkGrayBg        #111827
//   red               #FF0000
//   chipRed           #cb3837
//   orange            #ff8c00
//   chipYellow        #ffcd3f
//   chipBlue          #29abe2
//   chipMagenta       #c836c3
//   chipPurple        #8956ff

export const Sparkline = ({
  data = [],
  width = 120,
  height = 32,
  color = '#16ad3c',
  fill = true,
}) => {
  if (!data.length) return <svg width={width} height={height} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1 || 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M${points.join(' L')}`;
  const areaPath = `${path} L${width.toFixed(1)},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {fill && <path d={areaPath} fill={color} fillOpacity="0.10" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={width} cy={height - ((data[data.length - 1] - min) / range) * (height - 2) - 1} r="2.4" fill={color} />
    </svg>
  );
};

export const AreaChart = ({
  data = [],
  width = 600,
  height = 160,
  color = '#16ad3c',
  showAxis = true,
  yLabel = '',
  emptyLabel = 'No activity in window',
}) => {
  if (!data.length) {
    return (
      <div className="w-full flex items-center justify-center text-secondary text-sm border border-dashed border-border" style={{ height }}>
        {emptyLabel}
      </div>
    );
  }
  const padX = 40;
  const padY = 18;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const values = data.map(d => d.value);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;
  const stepX = data.length === 1 ? innerW : innerW / (data.length - 1);
  const pts = data.map((d, i) => {
    const x = padX + i * stepX;
    const y = padY + innerH - ((d.value - min) / range) * innerH;
    return [x, y];
  });
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${padY + innerH} L${pts[0][0].toFixed(1)},${padY + innerH} Z`;
  const yTicks = [0, 0.5, 1].map(t => ({
    y: padY + innerH - t * innerH,
    label: Math.round(min + range * t),
  }));
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block', fontFamily: 'Nunito, sans-serif' }}>
      {showAxis && yTicks.map((t, i) => (
        <g key={i}>
          <line x1={padX} y1={t.y} x2={width - padX} y2={t.y} stroke="#E0E6ED" strokeWidth="1" strokeDasharray={i === yTicks.length - 1 ? '0' : '2,3'} />
          <text x={padX - 6} y={t.y + 3} fontSize="10" fontWeight="700" fill="#888EA8" textAnchor="end" letterSpacing="0.06em">{t.label}</text>
        </g>
      ))}
      <defs>
        <linearGradient id={`area-grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#area-grad-${color.replace('#', '')})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <g key={i}>
          {i === pts.length - 1 && <circle cx={x} cy={y} r="3.2" fill={color} />}
          {showAxis && data[i].label && i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
            <text x={x} y={height - 4} fontSize="10" fontWeight="700" fill="#888EA8" textAnchor="middle" letterSpacing="0.06em">{data[i].label}</text>
          )}
        </g>
      ))}
      {yLabel && <text x={6} y={padY - 4} fontSize="9" fontWeight="800" fill="#888EA8" letterSpacing="0.14em">{yLabel}</text>}
    </svg>
  );
};

export const BarColumns = ({
  data = [],
  width = 600,
  height = 180,
  colorA = '#16ad3c',
  colorB = '#ff8c00',
  emptyLabel = 'No data in window',
}) => {
  if (!data.length) {
    return (
      <div className="w-full flex items-center justify-center text-secondary text-sm border border-dashed border-border" style={{ height }}>
        {emptyLabel}
      </div>
    );
  }
  const padX = 36;
  const padY = 22;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const stepX = innerW / data.length;
  const barW = Math.min(stepX * 0.34, 26);
  const max = Math.max(...data.flatMap(d => [d.a || 0, d.b || 0]), 1);
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block', fontFamily: 'Nunito, sans-serif' }}>
      <line x1={padX} y1={padY + innerH + 0.5} x2={width - padX} y2={padY + innerH + 0.5} stroke="#E0E6ED" strokeWidth="1" />
      {[0.25, 0.5, 0.75, 1].map((t, i) => (
        <g key={i}>
          <line x1={padX} y1={padY + innerH - t * innerH} x2={width - padX} y2={padY + innerH - t * innerH} stroke="#F2F4F7" strokeWidth="1" strokeDasharray="2,3" />
          <text x={padX - 6} y={padY + innerH - t * innerH + 3} fontSize="10" fontWeight="700" fill="#888EA8" textAnchor="end" letterSpacing="0.04em">{Math.round(max * t)}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const cx = padX + stepX * (i + 0.5);
        const aH = ((d.a || 0) / max) * innerH;
        const bH = ((d.b || 0) / max) * innerH;
        return (
          <g key={i}>
            <rect x={cx - barW - 1} y={padY + innerH - aH} width={barW} height={aH} fill={colorA} rx="1.5" />
            <rect x={cx + 1} y={padY + innerH - bH} width={barW} height={bH} fill={colorB} fillOpacity="0.9" rx="1.5" />
            <text x={cx} y={height - 6} fontSize="10" fontWeight="700" fill="#888EA8" textAnchor="middle" letterSpacing="0.04em">{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

export const Donut = ({
  data = [],
  size = 140,
  thickness = 18,
  centerLabel = '',
  centerValue = '',
}) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = size / 2 - thickness / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ fontFamily: 'Nunito, sans-serif' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F2F4F7" strokeWidth={thickness} />
      {data.map((d, i) => {
        const startAngle = (acc / total) * 2 * Math.PI - Math.PI / 2;
        acc += d.value;
        const endAngle = (acc / total) * 2 * Math.PI - Math.PI / 2;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const large = endAngle - startAngle > Math.PI ? 1 : 0;
        const path = `M${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)}`;
        return <path key={i} d={path} fill="none" stroke={d.color} strokeWidth={thickness} strokeLinecap="butt" />;
      })}
      <text x={cx} y={cy - 2} fontSize="24" fontWeight="800" fill="#111827" textAnchor="middle" style={{ fontVariantNumeric: 'tabular-nums' }}>{centerValue}</text>
      <text x={cx} y={cy + 16} fontSize="9" fontWeight="800" fill="#888EA8" textAnchor="middle" letterSpacing="0.14em">{centerLabel}</text>
    </svg>
  );
};

export const Gauge = ({
  value = 0,
  max = 100,
  size = 160,
  thickness = 14,
  color = '#16ad3c',
  label = '',
  unit = '%',
}) => {
  const pct = Math.max(0, Math.min(value / max, 1));
  const r = size / 2 - thickness / 2 - 2;
  const cx = size / 2;
  const cy = size / 2 + 12;
  const start = Math.PI;
  const end = 0;
  const arcAngle = start + (end - start) * pct;
  const x1 = cx + r * Math.cos(start);
  const y1 = cy + r * Math.sin(start);
  const x2 = cx + r * Math.cos(end);
  const y2 = cy + r * Math.sin(end);
  const xc = cx + r * Math.cos(arcAngle);
  const yc = cy + r * Math.sin(arcAngle);
  const trackPath = `M${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 0 1 ${x2.toFixed(2)},${y2.toFixed(2)}`;
  const fillPath = `M${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${xc.toFixed(2)},${yc.toFixed(2)}`;
  return (
    <svg width={size} height={size * 0.62 + 20} viewBox={`0 0 ${size} ${size * 0.62 + 20}`} style={{ fontFamily: 'Nunito, sans-serif' }}>
      <path d={trackPath} fill="none" stroke="#F2F4F7" strokeWidth={thickness} strokeLinecap="round" />
      <path d={fillPath} fill="none" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
      <text x={cx} y={cy - 4} fontSize="30" fontWeight="800" fill={color} textAnchor="middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}<tspan fontSize="14" fontWeight="700" fill="#888EA8" dx="2">{unit}</tspan>
      </text>
      <text x={cx} y={cy + 14} fontSize="9" fontWeight="800" fill="#888EA8" textAnchor="middle" letterSpacing="0.14em">{label}</text>
    </svg>
  );
};

export const Bullet = ({ value, max, color = '#34C759', height = 8 }) => {
  const pct = Math.min(100, (value / (max || 1)) * 100);
  return (
    <div className="w-full bg-border overflow-hidden" style={{ height, borderRadius: 2 }}>
      <div className="h-full" style={{ width: `${pct}%`, background: color, borderRadius: 2 }} />
    </div>
  );
};

export const StatusDot = ({ ok = true, pulse = false }) => (
  <span className="relative inline-flex" style={{ width: 8, height: 8 }}>
    <span
      className="absolute inset-0 rounded-full"
      style={{ background: ok ? '#34C759' : '#cb3837', boxShadow: ok ? '0 0 0 2px #28a74520' : '0 0 0 2px #cb383726' }}
    />
    {pulse && (
      <span
        className="absolute inset-0 rounded-full animate-ping"
        style={{ background: ok ? '#34C759' : '#cb3837', opacity: 0.55 }}
      />
    )}
  </span>
);
